// Card (DESIGN §3.8.1, N6): Fläche `card`, Radius 16, kein Rand, kein Schatten (beide Themes), Innenabstand 16.
import { Slot } from 'radix-ui';
import { type ComponentPropsWithRef, type ReactNode } from 'react';
import { cn } from './cn';
import { cva, type VariantProps } from './cva';

export const cardVariants = cva('relative min-w-0 rounded-xl', {
  variants: {
    variant: {
      default: 'bg-card',
      interactive:
        'bg-card transition-colors duration-(--bar-dur-fast) ease-standard hover:bg-card-hover cursor-pointer',
      tile: 'bg-tile',
      stage: 'rounded-lg bg-stage',
    },
    padding: { none: 'p-0', md: 'p-4', lg: 'p-6' },
  },
  defaultVariants: { variant: 'default', padding: 'md' },
});

export type CardProps = ComponentPropsWithRef<'div'> &
  VariantProps<typeof cardVariants> & {
    /** Rendert das Kind (z. B. einen Link) mit den Karten-Klassen (Radix Slot). */
    asChild?: boolean;
  };

export function Card({ variant, padding, asChild, className, ...rest }: CardProps) {
  const Component = asChild ? Slot.Root : 'div';
  return (
    <Component
      data-variant={variant ?? 'default'}
      data-surface={variant === 'tile' || variant === 'stage' ? undefined : 'card'}
      className={cardVariants({ variant, padding, className })}
      {...rest}
    />
  );
}

export interface CardHeaderProps extends Omit<ComponentPropsWithRef<'div'>, 'title'> {
  title: ReactNode;
  /** Überschrift-Ebene des Titels (Standard h2). */
  level?: 2 | 3;
  /** Rechts: Link „See all“ oder IconButton. */
  action?: ReactNode;
  /** Stärkerer Kartentitel (`label-strong`) statt `body`. */
  strong?: boolean;
}

/** Kartenkopf: Titel links, Aktion rechts. */
export function CardHeader({
  title,
  level = 2,
  action,
  strong,
  className,
  ...rest
}: CardHeaderProps) {
  const Tag = level === 2 ? 'h2' : 'h3';
  return (
    <div
      className={cn('mb-3 flex min-h-control items-center justify-between gap-3', className)}
      {...rest}
    >
      <Tag
        className={cn(
          'min-w-0 truncate text-fg',
          strong ? 'text-label-strong' : 'text-body font-semibold',
        )}
      >
        {title}
      </Tag>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}
